import React, { useEffect, useState } from "react";
import api, { getApiRuntimeDiagnostics } from "@/api";

function pretty(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value || "");
  }
}

function formatSeconds(value) {
  const total = Number(value || 0);
  if (!Number.isFinite(total) || total <= 0) return "0m";
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function buildOwnershipSummary(payload) {
  const ownership = payload?.ownership || null;
  const backend = payload?.backend || null;
  const localSecurity = payload?.localSecurity || null;
  if (!ownership && !backend) return [];
  const runtimeOwnership =
    ownership?.runtime_ownership ||
    localSecurity?.runtime_ownership ||
    backend?.runtime_ownership ||
    null;
  const registry =
    ownership?.registry ||
    localSecurity?.runtime_registry ||
    backend?.runtime_registry ||
    {};
  const authorityStatus =
    localSecurity?.runtime_business_mode ||
    ownership?.authority_status ||
    ownership?.business_mode ||
    backend?.runtime_business_mode ||
    "unknown";
  const tunnelAllowed =
    ownership?.tunnel_allowed ??
    localSecurity?.runtime_hosting_allowed ??
    false;
  const tunnelBlockReason =
    ownership?.tunnel_block_reason ||
    ownership?.display_message ||
    localSecurity?.runtime_display_message ||
    backend?.runtime_display_message ||
    "";
  return [
    ["Registry reachable", registry?.reachable === true ? "Yes" : registry?.reachable === false ? "No" : "N/A"],
    ["Current generation", String(runtimeOwnership?.ownership_generation || registry?.ownership?.ownership_generation || 0)],
    ["Primary device id", runtimeOwnership?.primary_device_id || registry?.ownership?.primary_device_id || "Not set"],
    ["Primary device name", runtimeOwnership?.primary_device_name || registry?.ownership?.primary_device_name || "Not set"],
    ["This device id", localSecurity?.current_device_id || ownership?.current_device_id || "Unavailable"],
    ["This device name", localSecurity?.current_device_name || ownership?.current_device_name || "Unavailable"],
    ["Authority status", authorityStatus],
    ["Last registry confirmation", registry?.last_confirmed_at || "Never"],
    ["Offline grace remaining", formatSeconds(registry?.offline_grace_remaining_seconds)],
    ["Tunnel", tunnelAllowed ? "Allowed" : `Blocked${tunnelBlockReason ? `: ${tunnelBlockReason}` : ""}`],
  ];
}

function buildCloudSummary(payload) {
  const localSecurity = payload?.localSecurity || null;
  const cloud = localSecurity?.cloud_namespace || null;
  if (!cloud) return [];
  const namespacePrefix = [cloud.tenant_prefix, cloud.business_prefix, cloud.branch_prefix].filter(Boolean).join("/");
  return [
    ["Provider", cloud.provider || "Not configured"],
    ["Bucket", cloud.bucket || "Not configured"],
    ["Mode", cloud.mode || "off"],
    ["Namespace prefix", namespacePrefix || "Not provisioned"],
    ["Provisioned", cloud.provisioned ? "Yes" : "No"],
    ["Last write check", cloud.last_write_check || "Never"],
    ["Last read check", cloud.last_read_check || "Never"],
    ["Last error", cloud.last_error || "None"],
  ];
}

function buildTunnelSummary(payload) {
  const gateway = payload?.gateway || null;
  const business = payload?.gateway?.business || payload?.business || null;
  const tunnel = gateway?.tunnel || payload?.gateway?.tunnel || payload?.tunnel || {};
  const expectedHostname = tunnel?.expectedHostname || business?.configuredRemoteHost || "";
  const routeStatus =
    business?.remoteHostAvailable === true
      ? "Ready"
      : business?.remoteHostUnavailableReason
      ? `Blocked: ${business.remoteHostUnavailableReason}`
      : "Unavailable";
  return [
    ["Gateway port", gateway?.port || "Unknown"],
    ["Expected hostname", expectedHostname || "Not available"],
    ["Configured host", business?.configuredRemoteHost || "Not configured"],
    ["Route status", routeStatus],
    ["Tunnel state", payload?.gateway?.states?.tunnel || "Unknown"],
    ["Tunnel config path", tunnel?.configPath || "Not written yet"],
    ["Tunnel last error", tunnel?.lastError || "None"],
    ["Tunnel start block", tunnel?.startBlockedReason || "None"],
  ];
}

export default function RuntimeDiagnostics() {
  const [data, setData] = useState({ loading: true, source: "", payload: null, error: "" });

  useEffect(() => {
    let timer = null;
    let mounted = true;
    const poll = async () => {
      try {
        const [backendRes, localSecurityRes, electronPayload, ownershipPayload] = await Promise.all([
          api.get("/auth/runtime-connectivity-diagnostics", { timeout: 6000 }),
          api.get("/local-security/status", { timeout: 6000 }),
          typeof window !== "undefined" && window.electronAPI?.getRuntimeDiagnostics
            ? window.electronAPI.getRuntimeDiagnostics().catch(() => null)
            : Promise.resolve(null),
          typeof window !== "undefined" && window.electronAPI?.getRuntimeOwnershipStatus
            ? window.electronAPI.getRuntimeOwnershipStatus().catch(() => null)
            : Promise.resolve(null),
        ]);
        const payload = {
          frontend: getApiRuntimeDiagnostics(),
          backend: backendRes?.data || null,
          localSecurity: localSecurityRes?.data || null,
          gateway: electronPayload,
          ownership: ownershipPayload?.ownership || null,
        };
        if (!mounted) return;
        setData({
          loading: false,
          source: electronPayload ? "frontend+backend+gateway" : "frontend+backend",
          payload,
          error: "",
        });
      } catch (e) {
        if (!mounted) return;
        setData({
          loading: false,
          source: "",
          payload: null,
          error: e?.response?.data?.detail || e?.message || "Failed to load diagnostics",
        });
      }
    };
    poll();
    timer = setInterval(poll, 5000);
    return () => {
      mounted = false;
      if (timer) clearInterval(timer);
    };
  }, []);

  return (
    <div className="p-4 space-y-3">
      <div className="text-lg font-semibold">Runtime Diagnostics</div>
      {data.loading ? <div className="text-sm text-muted-foreground">Loading...</div> : null}
      {data.error ? <div className="text-sm text-red-600">{data.error}</div> : null}
      {data.payload ? (
        <div className="rounded-lg border bg-card p-3">
          <div className="mb-2 text-sm font-medium">Ownership Registry</div>
          <div className="grid gap-2 md:grid-cols-2">
            {buildOwnershipSummary(data.payload).map(([label, value]) => (
              <div key={label} className="rounded-md border bg-background/70 p-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className="text-sm break-all">{value}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {data.payload ? (
        <div className="rounded-lg border bg-card p-3">
          <div className="mb-2 text-sm font-medium">Cloud Namespace</div>
          <div className="grid gap-2 md:grid-cols-2">
            {buildCloudSummary(data.payload).map(([label, value]) => (
              <div key={label} className="rounded-md border bg-background/70 p-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className="text-sm break-all">{value}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {data.payload ? (
        <div className="rounded-lg border bg-card p-3">
          <div className="mb-2 text-sm font-medium">Gateway / Tunnel</div>
          <div className="grid gap-2 md:grid-cols-2">
            {buildTunnelSummary(data.payload).map(([label, value]) => (
              <div key={label} className="rounded-md border bg-background/70 p-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className="text-sm break-all">{value}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {data.payload ? (
        <div className="rounded-lg border bg-card p-3">
          <div className="text-xs text-muted-foreground mb-2">Source: {data.source}</div>
          <pre className="text-xs whitespace-pre-wrap break-all">{pretty(data.payload)}</pre>
        </div>
      ) : null}
    </div>
  );
}
